/* ============================================================================
   CourseForm — criar / editar curso, com upload de capa, cálculo de duração
   e pré-visualização ao vivo.
   ============================================================================ */
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { API_CONFIG } from '../../config/api';
import AdminLayout from '../components/AdminLayout';
import { Field, TextInput, TextArea } from '../components/Field';
import { toast } from '../components/Toast';
import { IconBack, IconClock, IconImage, IconCheck, IconFolder } from '../components/icons';
import './CourseForm.css';

const CATEGORIES = ['AWS', 'DevOps', 'Kubernetes', 'Docker', 'Terraform', 'CI/CD', 'Linux', 'Segurança', 'Monitoring', 'Cloud'];

const EMPTY = { title: '', description: '', duration: '', category: '', folderName: '', coverImage: '', isFree: true, planIds: [] };

export default function CourseForm() {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const isEdit = !!courseId;
  const fileRef = useRef(null);

  const [form, setForm] = useState(EMPTY);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [calcDuration, setCalcDuration] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/admin/plans', true);
        if (r.success) setPlans(r.data || []);
      } catch { /* silencioso */ }
      if (courseId) loadCourse();
    })();
  }, [courseId]); // eslint-disable-line

  const loadCourse = async () => {
    try {
      setLoading(true);
      const r = await api.get('/admin/courses', true);
      if (r.success) {
        const c = (r.data || []).find((x) => (x.id || x.PK?.replace('COURSE#', '')) === courseId);
        if (c) setForm({
          title: c.title || '', description: c.description || '', duration: c.duration || '',
          category: c.category || '', folderName: c.folderName || '', coverImage: c.coverImage || '',
          isFree: c.isFree ?? !c.planIds?.length, planIds: c.planIds || [],
        });
      }
    } catch { toast.error('Erro ao carregar o curso.'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (isEdit) await api.put(`/admin/courses/${courseId}`, form, true);
      else await api.post('/admin/courses', form, true);
      setSaved(true);
      toast.success(isEdit ? 'Curso atualizado.' : 'Curso publicado.');
      setTimeout(() => navigate('/treinamentos/manager/courses'), 900);
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar o curso.');
      setSaving(false);
    }
  };

  /* ── Upload da capa ── */
  const uploadImage = async (file) => {
    if (!file) return;
    if (!form.folderName) { toast.error('Preencha a pasta no S3 antes de enviar a capa.'); return; }
    try {
      setUploadingImage(true);
      const base64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result.split(',')[1]);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const r = await api.post('/admin/upload-url', { fileName: file.name, fileBase64: base64 }, true);
      if (!r.success) throw new Error('Falha no upload.');
      set('coverImage', r.imageUrl);
      toast.success('Capa enviada.');
    } catch (err) {
      toast.error(err.message || 'Erro no upload da imagem.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith('image/')) uploadImage(file);
  };

  /* ── Cálculo automático de duração ── */
  const calculateDuration = async () => {
    if (!form.folderName) { toast.error('Informe a pasta no S3 primeiro.'); return; }
    setCalcDuration(true);
    try {
      const r = await fetch(`${API_CONFIG.BASE_URL}/videos/list?folderName=${form.folderName}`);
      if (!r.ok) throw new Error('Pasta não encontrada.');
      const result = await r.json();
      const videos = result.data?.videos || [];
      if (!videos.length) { toast.error('Nenhum vídeo na pasta.'); return; }

      let total = 0;
      for (const key of videos) {
        const s = await api.post('/videos/signed-url', { folderName: form.folderName, videoKey: key }, false);
        total += await new Promise((res, rej) => {
          const v = document.createElement('video');
          v.preload = 'metadata';
          v.onloadedmetadata = () => res(v.duration);
          v.onerror = () => rej(new Error('Erro ao ler vídeo.'));
          v.src = s.url;
        });
      }
      const h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60);
      set('duration', h > 0 ? (m > 0 ? `${h}h ${m}min` : `${h}h`) : `${m}min`);
      toast.success('Duração calculada.');
    } catch {
      toast.error('Erro ao calcular duração. Verifique a pasta.');
    } finally {
      setCalcDuration(false);
    }
  };

  /* ── Planos ── */
  const togglePlan = (id) => setForm((f) => {
    const planIds = f.planIds.includes(id) ? f.planIds.filter((x) => x !== id) : [...f.planIds, id];
    return { ...f, planIds, isFree: planIds.length ? false : f.isFree };
  });
  const toggleFree = () => setForm((f) => ({ ...f, isFree: !f.isFree, planIds: !f.isFree ? [] : f.planIds }));

  const fields = [form.title, form.description, form.category, form.folderName, form.duration, form.isFree || form.planIds.length, form.coverImage];
  const completeness = Math.round((fields.filter(Boolean).length / fields.length) * 100);

  if (loading && !form.title) {
    return <AdminLayout><div className="mg-loading"><div className="mg-spinner" /></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <button className="cf-breadcrumb" onClick={() => navigate('/treinamentos/manager/courses')}>
        <IconBack size={15} /> Cursos
      </button>
      <div className="cf-title-row">
        <h1 className="mg-page-title">{isEdit ? 'Editar curso' : 'Novo curso'}</h1>
        {isEdit && <span className="cf-edit-badge">Editando</span>}
      </div>
      <p className="mg-page-sub" style={{ marginBottom: 28 }}>
        {isEdit ? 'Atualize as informações do curso.' : 'Preencha os dados para publicar um novo curso.'}
      </p>

      <div className="cf-layout">
        <form className="cf-form" onSubmit={handleSubmit}>
          {/* ── Identidade ── */}
          <section className="mg-card">
            <h2 className="cf-card-title"><span className="cf-num">01</span> Identidade</h2>
            <Field label="Título" required>
              <TextInput value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Ex: AWS para DevOps — do Zero ao Avançado" required />
            </Field>
            <Field label="Descrição" required hint={`${form.description.length} caracteres`}>
              <TextArea value={form.description} onChange={(e) => set('description', e.target.value)} rows={4} placeholder="Descreva o que o aluno vai aprender…" required />
            </Field>
            <Field label="Categoria" required>
              <div className="cf-chips">
                {CATEGORIES.map((cat) => (
                  <button type="button" key={cat} className={`cf-chip${form.category === cat ? ' is-active' : ''}`} onClick={() => set('category', cat)}>{cat}</button>
                ))}
              </div>
              <TextInput style={{ marginTop: 10 }} value={form.category} onChange={(e) => set('category', e.target.value)} placeholder="Ou digite uma categoria personalizada" />
            </Field>
          </section>

          {/* ── Acesso ── */}
          <section className="mg-card">
            <h2 className="cf-card-title"><span className="cf-num">02</span> Acesso e publicação</h2>
            <div className="mg-field-row">
              <Field label="Pasta no S3" required hint="Letras minúsculas, números e hífens">
                <div className="cf-input-icon">
                  <IconFolder size={16} />
                  <TextInput value={form.folderName} onChange={(e) => set('folderName', e.target.value)} placeholder="introducao-aws-devops" required />
                </div>
              </Field>
              <Field label="Duração total" hint="Informe a pasta e clique em calcular">
                <div className="cf-duration">
                  <TextInput value={form.duration} readOnly placeholder="Auto-calculado" />
                  <button type="button" className="mg-btn mg-btn-ghost mg-btn-sm" onClick={calculateDuration} disabled={!form.folderName || calcDuration}>
                    {calcDuration ? <span className="mg-spinner mg-spinner-sm" /> : <IconClock size={15} />}
                    {calcDuration ? 'Calculando…' : 'Calcular'}
                  </button>
                </div>
              </Field>
            </div>
            <Field label="Plano de acesso" required hint="Selecione os planos com acesso a este curso">
              <div className="cf-plans">
                <button type="button" className={`cf-plan cf-plan-free${form.isFree ? ' is-active' : ''}`} onClick={toggleFree}>
                  <span>Gratuito</span>
                  {form.isFree && <IconCheck size={14} />}
                </button>
                {plans.map((plan) => {
                  const active = form.planIds.includes(plan.id);
                  return (
                    <button
                      type="button" key={plan.id}
                      className={`cf-plan${active ? ' is-active' : ''}`}
                      style={active ? { background: `${plan.color || '#d17d34'}22`, borderColor: plan.color || '#d17d34', color: plan.color || '#e8bb82' } : undefined}
                      onClick={() => togglePlan(plan.id)}
                    >
                      <span>{plan.name}</span>
                      {active && <IconCheck size={14} />}
                    </button>
                  );
                })}
              </div>
            </Field>
          </section>

          {/* ── Capa ── */}
          <section className="mg-card">
            <h2 className="cf-card-title"><span className="cf-num">03</span> Imagem de capa</h2>
            {form.coverImage ? (
              <div className="cf-cover">
                <img src={form.coverImage} alt="Capa do curso" />
                <div className="cf-cover-actions">
                  <button type="button" className="mg-btn mg-btn-ghost mg-btn-sm" onClick={() => fileRef.current?.click()}>Trocar</button>
                  <button type="button" className="mg-btn mg-btn-danger mg-btn-sm" onClick={() => set('coverImage', '')}>Remover</button>
                </div>
              </div>
            ) : (
              <div
                className={`cf-dropzone${dragOver ? ' is-over' : ''}${!form.folderName ? ' is-disabled' : ''}`}
                onClick={() => form.folderName && fileRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); form.folderName && setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
              >
                {uploadingImage ? (
                  <><span className="mg-spinner" /><p>Enviando imagem…</p></>
                ) : (
                  <>
                    <div className="cf-dropzone-icon"><IconImage size={28} /></div>
                    <p className="cf-dropzone-title">{dragOver ? 'Solte aqui!' : 'Arraste ou clique para enviar'}</p>
                    <p className="cf-dropzone-hint">PNG, JPG ou WEBP · proporção 16:9 recomendada</p>
                    {!form.folderName && <p className="cf-dropzone-warn">Preencha a pasta no S3 primeiro</p>}
                  </>
                )}
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => uploadImage(e.target.files[0])} />
          </section>

          <div className="cf-actions">
            <button type="button" className="mg-btn mg-btn-ghost" onClick={() => navigate('/treinamentos/manager/courses')}>Cancelar</button>
            <button type="submit" className="mg-btn mg-btn-primary" disabled={saving || saved}>
              {saved ? <><IconCheck /> Salvo!</> : saving ? <><span className="mg-spinner mg-spinner-sm" /> Salvando…</> : (isEdit ? 'Salvar alterações' : 'Publicar curso')}
            </button>
          </div>
        </form>

        {/* ── Preview ── */}
        <aside className="cf-preview">
          <p className="cf-preview-label">Pré-visualização</p>
          <div className="cf-preview-card">
            <div className="cf-preview-cover">
              {form.coverImage ? <img src={form.coverImage} alt="capa" /> : <div className="cf-preview-cover-empty"><IconImage size={26} /></div>}
              {form.category && <span className="cf-preview-cat">{form.category}</span>}
              <span className={`mg-badge ${form.isFree ? 'mg-badge-free' : ''}`} style={{ position: 'absolute', bottom: 10, left: 10, ...(form.isFree ? {} : { background: '#d17d3422', color: '#e8bb82', borderColor: '#d17d3455' }) }}>
                {form.isFree ? 'Gratuito' : (plans.find((p) => form.planIds.includes(p.id))?.name || 'Pago')}
              </span>
            </div>
            <div className="cf-preview-body">
              <h3>{form.title || <span className="cf-preview-ph">Título do curso…</span>}</h3>
              <p>{form.description ? form.description.slice(0, 100) + (form.description.length > 100 ? '…' : '') : <span className="cf-preview-ph">Descrição do curso…</span>}</p>
              {form.duration && <div className="cf-preview-meta"><IconClock size={13} /> {form.duration}</div>}
            </div>
          </div>

          <div className="cf-completeness">
            <div className="cf-completeness-head"><span>Completude</span><span>{completeness}%</span></div>
            <div className="cf-completeness-bar"><div style={{ width: `${completeness}%` }} /></div>
          </div>
        </aside>
      </div>
    </AdminLayout>
  );
}
